import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Search, Plus } from 'lucide-react'
import { ProductModel } from '@/http/models/Product/Product'
import { ProductModel as ProductType } from '@/types/model/Product'
import { ProductVariationModel } from '@/types/model/ProductVariation'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { TextInput, SelectInput } from '@/components/atoms'
import { Input } from '@/components/ui/input'
import { getFileUrl, stringifyGraph } from '@/clients/http'

import { useBusiness } from '@/contexts/BusinessContext'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

interface AddItemDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onAdd: (data: any) => Promise<void>
    loading: boolean
}

interface FormState {
    product_variation_id: string
    quantity: number
    discount?: number
    discount_type?: 'cash' | 'percentage'
}

export const AddItemDialog = ({
    open,
    onOpenChange,
    onAdd,
    loading,
}: AddItemDialogProps) => {
    const { formatCurrency } = useBusiness()
    const [search, setSearch] = useState('')
    const [products, setProducts] = useState<ProductType.Product[]>([])
    const [searching, setSearching] = useState(false)
    const [selectedProduct, setSelectedProduct] = useState<ProductType.Product | null>(null)

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormState>({
        defaultValues: {
            product_variation_id: '',
            quantity: 1,
            discount: 0,
            discount_type: 'cash',
        },
    })

    const productVariationId = watch('product_variation_id')

    useEffect(() => {
        if (!open) {
            setSearch('')
            setProducts([])
            setSelectedProduct(null)
            reset()
        }
    }, [open, reset])

    useEffect(() => {
        const fetchProducts = async () => {
            if (search.length < 2) {
                setProducts([])
                return
            }
            setSearching(true)
            const response = await ProductModel.getList({
                query: {
                    search,
                    page: 1,
                    length: 10,
                    graph_enc: stringifyGraph({
                        '*': true,
                        thumbnail: {
                            path: true,
                            versions: true,
                        },
                        category: {
                            name: true,
                        },
                        variations: {
                            _id: true,
                            regular_price: true,
                            purchase_cost: true,
                            initial_stock_quantity: true,
                            status: true,
                            name: true,
                        },
                    }),
                }
            })
            if (response.success) {
                setProducts(response.data.result)
            }
            setSearching(false)
        }

        const timer = setTimeout(fetchProducts, 500)
        return () => clearTimeout(timer)
    }, [search])

    const onSubmit = (data: FormState) => {
        onAdd(data)
    }

    useEnterSubmit(handleSubmit(onSubmit), !!(open && selectedProduct && productVariationId))

    const handleSelectProduct = (product: ProductType.Product) => {
        setSelectedProduct(product)
        if (product.variations && product.variations.length > 0) {
            if (product.variations.length === 1) {
                setValue('product_variation_id', product.variations[0]._id)
            } else {
                setValue('product_variation_id', '')
            }
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent size="md">
                <DialogHeader>
                    <DialogTitle>Add item</DialogTitle>
                    <DialogDescription>
                        Search for a product to add to this order.
                    </DialogDescription>
                </DialogHeader>

                {!selectedProduct ? (
                    <div className="space-y-3">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search products"
                                aria-label="Search products"
                                className="pl-8"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            {searching && (
                                <Spinner className="absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                            )}
                        </div>

                        <div className="divide-y divide-border-subtle overflow-hidden rounded-lg border border-border">
                            {products.length === 0 && !searching && (
                                <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
                                    <Search className="size-6 text-muted-foreground" />
                                    <p className="text-sm text-text-2">
                                        {search.length < 2 ? 'Type at least 2 characters to search.' : 'No products match your search.'}
                                    </p>
                                </div>
                            )}
                            {products.map((product) => (
                                <div
                                    key={product._id}
                                    className="flex cursor-pointer items-center gap-3 p-3 transition-colors hover:bg-subdued"
                                    onClick={() => handleSelectProduct(product)}
                                >
                                    <div className="size-10 shrink-0 overflow-hidden rounded-md border border-border bg-subdued">
                                        {product.thumbnail && (
                                            <img
                                                src={getFileUrl(product.thumbnail.path)}
                                                alt={product.name}
                                                className="size-full object-cover"
                                            />
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-ink">{product.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {product.variations?.length || 0} variants
                                        </p>
                                    </div>
                                    <Button variant="secondary" size="sm">
                                        Select
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 rounded-lg border border-border bg-subdued p-3">
                            <div className="size-12 shrink-0 overflow-hidden rounded-md border border-border bg-card">
                                {selectedProduct.thumbnail && (
                                    <img
                                        src={getFileUrl(selectedProduct.thumbnail.path)}
                                        alt={selectedProduct.name}
                                        className="size-full object-cover"
                                    />
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-ink">{selectedProduct.name}</p>
                                <Button
                                    type="button"
                                    variant="link"
                                    className="text-sm"
                                    onClick={() => setSelectedProduct(null)}
                                >
                                    Change product
                                </Button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <SelectInput
                                control={control}
                                name="product_variation_id"
                                label="Variant"
                                options={selectedProduct.variations?.map((v) => ({
                                    label: `${v.name} - ${formatCurrency(v.regular_price)}`,
                                    value: v._id,
                                })) || []}
                                rules={{ required: 'Required' }}
                            />
                            <TextInput
                                control={control}
                                name="quantity"
                                label="Quantity"
                                type="number"
                                valueAsNumber
                                rules={{ required: 'Required', min: 1 }}
                                errors={errors}
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <TextInput
                                control={control}
                                name="discount"
                                label="Discount"
                                type="number"
                                valueAsNumber
                                errors={errors}
                                placeholder="0.00"
                            />
                            <SelectInput
                                control={control}
                                name="discount_type"
                                label="Discount type"
                                options={[
                                    { label: 'Fixed amount', value: 'cash' },
                                    { label: 'Percentage (%)', value: 'percentage' },
                                ]}
                            />
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        type="button"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmit(onSubmit)}
                        disabled={loading || !selectedProduct || !productVariationId}>
                        {loading ? (
                            <>
                                <Spinner />
                                Adding…
                            </>
                        ) : (
                            <>
                                <Plus />
                                Add item
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
